// pages/DailyReport/index.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { DailyReportList } from '../../components/DailyReport/DailyReportList';
import DailyReportSearchModal from '../../components/DailyReport/DailyReportSearchModal';
import { ReportListItem, DailyReportSearchParams } from '../../types/dailyReport';
import { dailyReportService } from '../../services/dailyReport';
import { Member } from '../../types/member';
import { Customer } from '../../types/customer';
import { memberService } from '../../services/member';
import { customerService } from '../../services/customer';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'; // 無所属メンバーアイコン用
import UnaffiliatedMemberReportDialog from '../../components/DailyReport/UnaffiliatedMemberReportDialog'; // 新しいコンポーネント


// ローカルストレージのキー
const STORAGE_KEY = 'dailyReportSearchParams';

const DailyReportListPage: React.FC = () => {
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  // 検索モーダル関連
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchParams, setSearchParams] = useState<DailyReportSearchParams | null>(null);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [lastSavedParams, setLastSavedParams] = useState<DailyReportSearchParams | null>(null);

  // 無所属メンバーレポート関連
  const [unaffiliatedReportOpen, setUnaffiliatedReportOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);



  const navigate = useNavigate();
  const location = useLocation();

  // location とローカルストレージを利用して初期化
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);

        // 検索条件を最初に読み込む
        const savedParams = localStorage.getItem(STORAGE_KEY);
        let searchActive = false;
        let params = null;

        // 保存された検索条件があれば、それを使う
        if (savedParams) {
          try {
            const parsedParams = JSON.parse(savedParams) as DailyReportSearchParams;
            if (Object.keys(parsedParams).length > 0) {
              console.log('保存された検索条件を使用:', parsedParams);
              params = parsedParams;
              searchActive = true;
            }
          } catch (e) {
            console.error('検索条件の解析エラー:', e);
          }
        }

        // 基本データを読み込む
        await Promise.all([loadMembers(), loadCustomers()]);

        // 検索条件に基づいてデータを読み込む
        if (searchActive && params) {
          console.log('検索条件あり、検索を実行:', params);
          setSearchParams(params);
          setIsSearchActive(true);
          await searchReports(params);
        } else {
          console.log('検索条件なし、全データ読み込み');
          setSearchParams(null);
          setIsSearchActive(false);
          await loadReports();
        }
      } catch (error) {
        console.error('初期化エラー:', error);
        setError('データの読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [location.key]); // location.key は他のページに遷移して戻ってきたときに変わる

  // 日報データの読み込み
  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dailyReportService.getAll();
      setReports(response.reports);
      return response.reports;
    } catch (error) {
      console.error('日報データ取得エラー:', error);
      setError('データの取得に失敗しました');
      return [];
    } finally {
      setLoading(false);
    }
  };

  // メンバーデータの読み込み
  const loadMembers = async () => {
    try {
      const membersData = await memberService.getAllMembers();
      setMembers(membersData);
      return membersData;
    } catch (error) {
      console.error('メンバー情報取得エラー:', error);
      return [];
    }
  };

  // 顧客データの読み込み
  const loadCustomers = async () => {
    try {
      const customersData = await customerService.getAllCustomers();
      setCustomers(customersData);
      return customersData;
    } catch (error) {
      console.error('顧客情報取得エラー:', error);
      return [];
    }
  };

  // 検索処理
  const searchReports = async (params: DailyReportSearchParams) => {
    // パラメータが空の場合は全データを読み込む
    if (!params || Object.keys(params).length === 0) {
      return loadReports();
    }

    try {
      console.log('検索実行開始:', params);
      setLoading(true);
      setError(null);

      const response = await dailyReportService.search(params);
      console.log('検索結果:', response.reports.length, '件取得');

      // データを更新
      setReports(response.reports);
      setPage(0); // 検索結果の1ページ目を表示

      // 検索状態を更新
      setIsSearchActive(true);

      return response.reports;
    } catch (error) {
      console.error('検索エラー:', error);
      setError('検索に失敗しました');
      return [];
    } finally {
      setLoading(false);
    }
  };

  // 検索実行
  const handleSearch = async (params: DailyReportSearchParams) => {
    // 空の条件をフィルタリング
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== '')
    ) as DailyReportSearchParams;

    // 検索条件の有無チェック
    const hasSearchParams = Object.keys(filteredParams).length > 0;

    // 検索条件をローカルストレージに保存または削除
    if (hasSearchParams) {
      const paramsJson = JSON.stringify(filteredParams);
      console.log('検索条件をローカルストレージに保存:', filteredParams);
      localStorage.setItem(STORAGE_KEY, paramsJson);
      setLastSavedParams(filteredParams);
    } else {
      console.log('検索条件をローカルストレージから削除');
      localStorage.removeItem(STORAGE_KEY);
      setLastSavedParams(null);
    }

    // 状態を更新
    setSearchParams(filteredParams);
    setIsSearchActive(hasSearchParams);

    // 即時検索を実行
    if (hasSearchParams) {
      console.log('検索を実行');
      await searchReports(filteredParams);
    } else {
      console.log('全データを読み込み');
      await loadReports();
    }
  };

  // 検索条件のリセット
  const handleResetSearch = async () => {
    console.log('検索条件をリセット');

    // ローカルストレージから検索条件を削除
    localStorage.removeItem(STORAGE_KEY);
    setLastSavedParams(null);

    // 状態をリセット
    setIsSearchActive(false);
    setSearchParams(null);

    // データを再読込
    await loadReports();
  };

  const handleEdit = (id: number) => {
    // ナビゲーション前に検索条件を保存
    if (isSearchActive && searchParams) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(searchParams));
      setLastSavedParams(searchParams);
    }
    navigate(`/daily-report/${id}/edit`);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('本当に削除しますか？')) {
      try {
        await dailyReportService.delete(id);

        // 現在の検索条件で再検索
        if (isSearchActive && searchParams) {
          await searchReports(searchParams);
        } else {
          // 検索条件がない場合は全件取得
          await loadReports();
        }
      } catch (error) {
        console.error('日報の削除に失敗しました:', error);
        setError('削除に失敗しました');
      }
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleNewClick = () => {
    // ナビゲーション前に検索条件を保存
    if (isSearchActive && searchParams) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(searchParams));
      setLastSavedParams(searchParams);
    }
    navigate('/construction');
    // navigate('/daily-report/new');
  };

  const handleToggleRowExpansion = (index: number) => {
    setExpandedRows(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // 検索モーダルを開く
  const handleOpenSearchModal = () => {
    setSearchModalOpen(true);
  };

  // 検索モーダルを閉じる
  const handleCloseSearchModal = () => {
    setSearchModalOpen(false);
  };


  // 無所属メンバーレポートダイアログを開く
  const handleOpenUnaffiliatedReport = () => {
    setUnaffiliatedReportOpen(true);
  };

  // 無所属メンバーレポートダイアログを閉じる
  const handleCloseUnaffiliatedReport = () => {
    setUnaffiliatedReportOpen(false);
  };


  return (
    <Container>
      <Box sx={{
        my: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Typography variant="h4" component="h1">
          日報一覧
        </Typography>

        <Box sx={{
          display: 'flex',
          gap: 2,
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Button
            variant="outlined"
            color="info"
            startIcon={<AssignmentIndIcon />}
            onClick={handleOpenUnaffiliatedReport}
            fullWidth
          >
            無所属メンバー
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={handleOpenSearchModal}
            fullWidth
          >
            検索
          </Button>

          {isSearchActive && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<FilterAltOffIcon />}
              onClick={handleResetSearch}
              fullWidth
            >
              条件クリア
            </Button>
          )}
        </Box>
      </Box>

      {/* 検索結果表示 */}
      {isSearchActive && (
        <Typography
          variant="subtitle1"
          sx={{
            mb: 2,
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <SearchIcon fontSize="small" />
          検索結果: {reports.length}件
        </Typography>
      )}

      <DailyReportList
        reports={reports}
        loading={loading}
        error={error}
        page={page}
        rowsPerPage={rowsPerPage}
        expandedRows={expandedRows}
        onToggleRowExpansion={handleToggleRowExpansion}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onNewClick={handleNewClick}
      />

      {/* 検索モーダル */}
      <DailyReportSearchModal
        open={searchModalOpen}
        onClose={handleCloseSearchModal}
        onSearch={handleSearch}
        members={members}
        customers={customers}
      />

      {/* 無所属メンバーレポートダイアログ */}
      <UnaffiliatedMemberReportDialog
        open={unaffiliatedReportOpen}
        onClose={handleCloseUnaffiliatedReport}
        year={selectedYear}
        month={selectedMonth}
        onYearChange={setSelectedYear}
        onMonthChange={setSelectedMonth}
      />
    </Container>
  );
};

export default DailyReportListPage;